import { authService } from '@/api/auth.service';
import type { ResetPasswordDto } from '@/entities/auth/reset-password.dto';
import type { SendPasswordResetMailDto } from '@/entities/auth/send-password-reset-mail.dto';
import { useMutation } from '@tanstack/vue-query';
import { type MutationDefaultsContext, queryClient } from '../query-client';
import type { ApiErrorResponse } from '@/entities/api-error';
import { createToast, withProps } from 'mosha-vue-toastify';
import ErrorToast from '@/components/ErrorToast.vue';
import { i18n } from '@/i18n';
import { invalidateAuthData } from '@/queries/auth/use-login.ts';

const SEND_PASSWORD_RESET_MAIL_QUERY_KEY = 'sendPasswordResetMail';
const RESET_PASSWORD_QUERY_KEY = 'resetPassword';

export const useSendPasswordResetMail = () => {
  return useMutation<unknown, unknown, SendPasswordResetMailDto, MutationDefaultsContext>({
    mutationKey: [SEND_PASSWORD_RESET_MAIL_QUERY_KEY],
    mutationFn: ({ email }) => authService.sendPasswordResetEmail(email),
    onMutate: () => ({
      successMessage: 'Password reset email sent',
      invalidateQueryKeys: [SEND_PASSWORD_RESET_MAIL_QUERY_KEY],
    }),
  });
};

export const useResetPassword = () => {
  return useMutation<void, unknown, ResetPasswordDto, MutationDefaultsContext>({
    mutationKey: [RESET_PASSWORD_QUERY_KEY],
    mutationFn: async ({ token, password }) => {
      try {
        return await authService.resetPassword(token, password);
      } catch (error) {
        const errorData = (error as ApiErrorResponse).response?.data;
        const message = errorData?.message;

        if (typeof message === 'string' && message === 'Password reset token expired') {
          // Mark error as handled to prevent default error handler from showing a toast
          (error as any).__handled = true;
          createToast(
            withProps(ErrorToast, {
              message: i18n.global.t('errors.passwordResetTokenExpired'),
            }),
            {
              type: 'danger',
              position: 'bottom-right',
            },
          );
        }
        throw error;
      }
    },
    onSuccess: async (result, variables, onMutateResult, context) => {
      queryClient.getMutationDefaults([])?.onSuccess?.(
        null,
        null,
        {
          successMessage: 'Password successfully reset',
          invalidateQueryKeys: [RESET_PASSWORD_QUERY_KEY],
        },
        context,
      );
      await invalidateAuthData();
    },
  });
};
